import { useState } from 'react';
import type { Action } from '~/lib/network/funnel-report';

interface ActionCardProps {
  action: Action;
  recipeName: string; // kept for potential future use (linking to findings)
}

const EFFORT_STYLES = {
  'Quick win': 'badge-success',
  'Medium effort': 'badge-warning',
  'Major project': 'badge-neutral',
};

export function ActionCard({ action, recipeName: _recipeName }: ActionCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const effortStyle = EFFORT_STYLES[action.effort];

  return (
    <div
      className={`collapse bg-base-100 border border-base-300 rounded-box ${
        isExpanded ? 'collapse-open' : ''
      }`}
    >
      <input
        type="checkbox"
        checked={isExpanded}
        onChange={(e) => setIsExpanded(e.target.checked)}
        className="min-h-0"
      />

      {/* Collapsed Header */}
      <div className="collapse-title min-h-0 py-4 pr-12">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-medium text-base-content leading-tight">
              {action.title}
            </p>
            <p className="text-sm text-base-content/60 mt-1">{action.impact}</p>
          </div>
          <span className={`badge ${effortStyle} shrink-0`}>{action.effort}</span>
        </div>
      </div>

      {/* Expanded Content */}
      <div className="collapse-content">
        <div className="pt-2 border-t border-base-200">
          {/* Steps */}
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-base-content/80 mb-2">
              Steps
            </h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-base-content/70">
              {action.steps.map((step, index) => (
                <li key={index} className="leading-relaxed">
                  {step}
                </li>
              ))}
            </ol>
          </div>

          {/* Tools */}
          {action.tools && action.tools.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-base-content/80 mb-2">
                Tools
              </h4>
              <div className="flex flex-wrap gap-2">
                {action.tools.map((tool, index) => (
                  <span
                    key={index}
                    className="badge badge-outline badge-sm text-base-content/70"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
